import { attributes, type HtmlValue, html } from 'BomdisoftComponents/html/index';
import { CLOSE_ROUNDED_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

export type TokenKeyState = { key: string; inputValue: string; tokenCount: number; showing: boolean; activeIndex: number; optionCount: number };

export type TokenKeyAction = { kind: 'close' } | { kind: 'commit' } | { kind: 'move'; index: number } | { kind: 'none' } | { kind: 'pick' } | { kind: 'removeLast' };

export const tokenKeyAction = ({ activeIndex, inputValue, key, optionCount, showing, tokenCount }: TokenKeyState): TokenKeyAction => {
  if (key === 'ArrowDown' || key === 'ArrowUp') {
    if (optionCount === 0) return { kind: 'none' };
    const delta = key === 'ArrowDown' ? 1 : -1;
    return { index: activeIndex < 0 ? (delta > 0 ? 0 : optionCount - 1) : (activeIndex + delta + optionCount) % optionCount, kind: 'move' };
  }
  if (key === 'Enter' || key === ',') {
    if (showing && activeIndex >= 0 && activeIndex < optionCount) return { kind: 'pick' };
    return inputValue.trim() === '' ? { kind: 'none' } : { kind: 'commit' };
  }
  if (key === 'Backspace') return inputValue === '' && tokenCount > 0 ? { kind: 'removeLast' } : { kind: 'none' };
  if (key === 'Escape') return showing ? { kind: 'close' } : { kind: 'none' };
  return { kind: 'none' };
};

export type TokenFieldToken = { key: string; label: string; mark?: HtmlValue; removeLabel: string };

export type TokenFieldOption = { key: string; label: string; mark?: HtmlValue; meta?: string; variant?: 'action' };

export type TokenFieldGroup = { key: string; label?: string; options: readonly TokenFieldOption[] };

export type TokenFieldTokensOptions = {
  disabled?: boolean;
  testId?: string;
  tokens: readonly TokenFieldToken[];
  tooltipIdOf: (index: number) => string;
};

export type TokenFieldSuggestionsOptions = {
  activeIndex?: number;
  listboxId: string;
  suggestions: readonly TokenFieldGroup[];
  testId?: string;
};

export type TokenFieldOptions = {
  disabled?: boolean;
  errorText?: string;
  helperText?: string;
  id: string;
  inline?: boolean;
  inputValue?: string;
  label: string;
  placeholder?: string;
  suggestions?: readonly TokenFieldGroup[];
  testId?: string;
  tokens: readonly TokenFieldToken[];
  tooltipIdOf?: (index: number) => string;
};

export const toTokenFieldOptionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

export const renderTokenFieldTokens = ({ disabled, testId, tokens, tooltipIdOf }: TokenFieldTokensOptions) =>
  html`${tokens.map(
    (token, index) =>
      html`<span${attributes({ class: 'bomdisoft-token-field-token', 'data-lead': Boolean(token.mark), 'data-testid': testId ? `${testId}-token-${token.key}` : undefined })}>${token.mark}<span class="bomdisoft-token-field-token-label">${token.label}</span>${disabled ? '' : renderTooltip({ children: html`<button${attributes({ 'aria-label': token.removeLabel, class: 'bomdisoft-token-field-remove', 'data-testid': testId ? `${testId}-remove-${token.key}` : undefined, type: 'button' })}>${renderIcon(CLOSE_ROUNDED_GLYPH, { size: 13 })}</button>`, id: tooltipIdOf(index), placement: 'top', title: token.removeLabel })}</span>`,
  )}`;

export const renderTokenFieldSuggestions = ({ activeIndex = -1, listboxId, suggestions, testId }: TokenFieldSuggestionsOptions) => {
  let optionIndex = -1;
  return html`${suggestions.map(
    (group, groupIndex) =>
      html`<fieldset${attributes({ 'aria-labelledby': group.label ? `${listboxId}-group-${groupIndex}` : undefined, class: 'bomdisoft-token-field-group', 'data-testid': testId ? `${testId}-group-${group.key}` : undefined })}>${group.label ? html`<div${attributes({ class: 'bomdisoft-token-field-group-label', id: `${listboxId}-group-${groupIndex}` })}>${group.label}</div>` : ''}${group.options.map(
        option => {
          optionIndex += 1;
          const isActive = optionIndex === activeIndex;
          return html`<div${attributes({ 'aria-selected': isActive ? 'true' : 'false', class: 'bomdisoft-token-field-option', 'data-active': isActive, 'data-testid': testId ? `${testId}-option-${option.key}` : undefined, 'data-variant': option.variant, id: toTokenFieldOptionId(listboxId, optionIndex), role: 'option', tabindex: '-1' })}>${option.mark}<span class="bomdisoft-token-field-option-words"><span>${option.label}</span>${option.meta ? html`<span class="bomdisoft-token-field-meta">${option.meta}</span>` : ''}</span></div>`;
        },
      )}</fieldset>`,
  )}`;
};

export const renderTokenField = ({ disabled, errorText, helperText, id, inline = false, inputValue = '', label, placeholder, suggestions, testId, tokens, tooltipIdOf = index => `${id}-remove-${index}-tip` }: TokenFieldOptions) => {
  const listboxId = `${id}-listbox`;
  const refused = errorText !== undefined && errorText !== '';
  const helper = refused ? errorText : helperText;
  const offersSuggestions = suggestions !== undefined;
  const input = {
    'aria-describedby': helper ? `${id}-helper` : undefined,
    'aria-invalid': refused ? 'true' : undefined,
    autocomplete: 'off',
    class: 'bomdisoft-token-field-input',
    'data-testid': testId ? `${testId}-input` : undefined,
    disabled,
    id: `${id}-input`,
    placeholder,
    value: inputValue,
  };
  const combobox = offersSuggestions ? { 'aria-autocomplete': 'list', 'aria-controls': listboxId, 'aria-expanded': 'false', role: 'combobox' } : {};
  return html`<div${attributes({ class: 'bomdisoft-token-field', 'data-inline': inline, 'data-testid': testId })}><label${attributes({ class: 'bomdisoft-token-field-label', 'data-disabled': disabled, 'data-error': refused, 'data-hidden': inline, for: `${id}-input` })}>${label}</label><div${attributes({ class: 'bomdisoft-token-field-field', 'data-disabled': disabled, 'data-error': refused })}>${renderTokenFieldTokens({ disabled, testId, tokens, tooltipIdOf })}<span${attributes({ class: 'bomdisoft-token-field-entry', 'data-placeholder': placeholder })}><input${attributes({ ...input, ...combobox })}></span></div>${helper ? html`<p${attributes({ class: 'bomdisoft-token-field-helper', 'data-disabled': disabled, 'data-error': refused, 'data-testid': testId ? `${testId}-helper` : undefined, id: `${id}-helper` })}>${helper}</p>` : ''}${offersSuggestions ? html`<div${attributes({ 'aria-label': label, class: 'bomdisoft-token-field-listbox', id: listboxId, popover: 'manual', role: 'listbox' })}>${renderTokenFieldSuggestions({ listboxId, suggestions, testId })}</div>` : ''}</div>`;
};
